<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Loader2, Check, ChevronsUpDown, Building2 } from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { API, HOST } from '@/utils/http'
import type { AdminUser, AdminOrganization } from '@/types/admin'

const getInitials = (member: AdminUser): string => {
  if (member.first_name && member.last_name) {
    return `${member.first_name[0]}${member.last_name[0]}`.toUpperCase()
  }
  if (member.display_name) {
    const parts = member.display_name.split(' ')
    return parts.length > 1
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : member.display_name.slice(0, 2).toUpperCase()
  }
  if (member.email) {
    return member.email.slice(0, 2).toUpperCase()
  }
  return '??'
}

const emit = defineEmits<{
  (e: 'customer-created'): void
}>()

// Dialog state
const open = ref(false)

// Form mode tab
const customerType = ref<'individual' | 'business'>('individual')

// Member state (individual)
const members = ref<AdminUser[]>([])
const loadingMembers = ref(false)
const selectedMemberId = ref<string>('')
const memberOpen = ref(false)

// Organization state (business)
const organizations = ref<AdminOrganization[]>([])
const loadingOrganizations = ref(false)
const selectedOrganizationId = ref<string>('')
const organizationOpen = ref(false)

// Primary contact fields (for business)
const contactEmail = ref('')
const contactFirstName = ref('')
const contactLastName = ref('')

// Common state
const creating = ref(false)
const error = ref<string | null>(null)

// Filter functions
const filterMembers = (list: AdminUser[], search: string) => {
  const term = search.toLowerCase()
  return list.filter(m =>
    (m.display_name?.toLowerCase().includes(term)) ||
    (m.email?.toLowerCase().includes(term)) ||
    (m.first_name?.toLowerCase().includes(term)) ||
    (m.last_name?.toLowerCase().includes(term)) ||
    (String(m.member_id).includes(term))
  )
}

const filterOrganizations = (list: AdminOrganization[], search: string) => {
  const term = search.toLowerCase()
  return list.filter(org =>
    (org.organization_name?.toLowerCase().includes(term)) ||
    (String(org.organization_id).includes(term))
  )
}

// Fetch members
const fetchMembers = async () => {
  loadingMembers.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/users`
    })
    members.value = response.data.users || response.data
  } catch (err: any) {
    console.error('Error fetching members:', err)
  } finally {
    loadingMembers.value = false
  }
}

// Fetch organizations
const fetchOrganizations = async () => {
  loadingOrganizations.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/organizations`
    })
    organizations.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching organizations:', err)
  } finally {
    loadingOrganizations.value = false
  }
}

// Get selected member
const selectedMember = computed(() => {
  if (!selectedMemberId.value) return null
  return members.value.find(m => m.member_id === parseInt(selectedMemberId.value))
})

// Get selected organization
const selectedOrganization = computed(() => {
  if (!selectedOrganizationId.value) return null
  return organizations.value.find(org => org.organization_id === parseInt(selectedOrganizationId.value))
})

// Check if form is valid
const isFormValid = computed(() => {
  if (customerType.value === 'business') {
    return selectedOrganizationId.value &&
      contactEmail.value.trim() &&
      contactFirstName.value.trim() &&
      contactLastName.value.trim()
  }
  return !!selectedMemberId.value
})

// Reset form
const resetForm = () => {
  customerType.value = 'individual'
  selectedMemberId.value = ''
  selectedOrganizationId.value = ''
  contactEmail.value = ''
  contactFirstName.value = ''
  contactLastName.value = ''
  error.value = null
}

// Create customer
const createCustomer = async () => {
  error.value = null

  if (customerType.value === 'business') {
    if (!selectedOrganizationId.value) {
      error.value = 'Please select an organization'
      return
    }
    if (!contactEmail.value.trim()) {
      error.value = 'Please enter primary contact email'
      return
    }
    if (!contactFirstName.value.trim()) {
      error.value = 'Please enter primary contact first name'
      return
    }
    if (!contactLastName.value.trim()) {
      error.value = 'Please enter primary contact last name'
      return
    }
  } else {
    if (!selectedMemberId.value) {
      error.value = 'Please select a member'
      return
    }
  }

  creating.value = true

  try {
    if (customerType.value === 'business') {
      await API({
        method: 'POST',
        url: `${HOST}/payments/bridge/organizations`,
        data: {
          organization_id: parseInt(selectedOrganizationId.value),
          email: contactEmail.value.trim(),
          first_name: contactFirstName.value.trim(),
          last_name: contactLastName.value.trim()
        }
      })
    } else {
      await API({
        method: 'POST',
        url: `${HOST}/payments/bridge/customers`,
        data: {
          member_id: parseInt(selectedMemberId.value)
        }
      })
    }

    emit('customer-created')
    open.value = false
    resetForm()
  } catch (err: any) {
    console.error('Error creating Bridge customer:', err)
    error.value = err.response?.data?.detail || 'Failed to create Bridge customer'
  } finally {
    creating.value = false
  }
}

// Init - fetch both lists
fetchMembers()
fetchOrganizations()
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <Button>
        New Customer
      </Button>
    </DialogTrigger>
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Create Bridge Customer</DialogTitle>
        <DialogDescription>
          Onboard a new individual or business customer to Bridge.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <!-- Error message -->
        <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {{ error }}
        </div>

        <!-- Customer type tabs -->
        <Tabs v-model="customerType" class="w-full">
          <TabsList class="grid w-full grid-cols-2">
            <TabsTrigger value="individual">Individual</TabsTrigger>
            <TabsTrigger value="business">Business</TabsTrigger>
          </TabsList>

          <!-- Individual Tab -->
          <TabsContent value="individual" class="mt-4">
            <div class="space-y-4">
              <div>
                <Label class="mb-2 block">Member</Label>
                <Popover v-model:open="memberOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      role="combobox"
                      :aria-expanded="memberOpen"
                      class="w-full justify-between"
                      :disabled="loadingMembers"
                    >
                      <span v-if="loadingMembers">Loading members...</span>
                      <span v-else-if="selectedMember" class="flex items-center gap-2 truncate">
                        <Avatar class="h-5 w-5 flex-shrink-0">
                          <AvatarImage v-if="selectedMember.avatar_url" :src="selectedMember.avatar_url" />
                          <AvatarFallback class="text-xs">{{ getInitials(selectedMember) }}</AvatarFallback>
                        </Avatar>
                        <span class="font-medium text-gray-900">
                          {{ selectedMember.display_name || selectedMember.first_name || 'Unnamed' }}
                        </span>
                        <span class="text-xs text-gray-400">
                          #{{ selectedMember.member_id }}
                        </span>
                      </span>
                      <span v-else class="text-muted-foreground">Select a member...</span>
                      <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-[400px] p-0">
                    <Command :filter-function="filterMembers">
                      <CommandInput placeholder="Search by name, email, or ID..." />
                      <CommandList>
                        <CommandEmpty>No member found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="member in members"
                            :key="member.member_id"
                            :value="member"
                            @select="() => {
                              selectedMemberId = String(member.member_id)
                              memberOpen = false
                            }"
                          >
                            <Check
                              :class="cn(
                                'mr-2 h-4 w-4',
                                selectedMemberId === String(member.member_id) ? 'opacity-100' : 'opacity-0'
                              )"
                            />
                            <div class="flex items-center gap-2 flex-1 min-w-0">
                              <Avatar class="h-6 w-6 flex-shrink-0">
                                <AvatarImage v-if="member.avatar_url" :src="member.avatar_url" :alt="member.display_name || ''" />
                                <AvatarFallback class="text-xs bg-gray-100">
                                  {{ getInitials(member) }}
                                </AvatarFallback>
                              </Avatar>
                              <span class="font-medium text-gray-900 truncate">
                                {{ member.display_name || member.first_name || 'Unnamed' }}
                              </span>
                              <span class="text-xs text-gray-500 truncate">
                                {{ member.email || '' }}
                              </span>
                              <span class="text-xs text-gray-400 flex-shrink-0">
                                #{{ member.member_id }}
                              </span>
                            </div>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </TabsContent>

          <!-- Business Tab -->
          <TabsContent value="business" class="mt-4">
            <div class="space-y-4">
              <div>
                <Label class="mb-2 block">Organization</Label>
                <Popover v-model:open="organizationOpen">
                  <PopoverTrigger as-child>
                    <Button
                      variant="outline"
                      role="combobox"
                      :aria-expanded="organizationOpen"
                      class="w-full justify-between"
                      :disabled="loadingOrganizations"
                    >
                      <span v-if="loadingOrganizations">Loading organizations...</span>
                      <span v-else-if="selectedOrganization" class="flex items-center gap-2 truncate">
                        <Building2 class="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <span class="font-medium text-gray-900">
                          {{ selectedOrganization.organization_name }}
                        </span>
                        <span class="text-xs text-gray-400">
                          #{{ selectedOrganization.organization_id }}
                        </span>
                      </span>
                      <span v-else class="text-muted-foreground">Select an organization...</span>
                      <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-[400px] p-0">
                    <Command :filter-function="filterOrganizations">
                      <CommandInput placeholder="Search by name or ID..." />
                      <CommandList>
                        <CommandEmpty>No organization found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="org in organizations"
                            :key="org.organization_id"
                            :value="org"
                            @select="() => {
                              selectedOrganizationId = String(org.organization_id)
                              organizationOpen = false
                            }"
                          >
                            <Check
                              :class="cn(
                                'mr-2 h-4 w-4',
                                selectedOrganizationId === String(org.organization_id) ? 'opacity-100' : 'opacity-0'
                              )"
                            />
                            <div class="flex items-center gap-2 flex-1 min-w-0">
                              <Building2 class="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <span class="font-medium text-gray-900 truncate">
                                {{ org.organization_name }}
                              </span>
                              <span class="text-xs text-gray-400 flex-shrink-0">
                                #{{ org.organization_id }}
                              </span>
                              <span v-if="org.current_plan" class="text-xs text-blue-500 flex-shrink-0">
                                {{ org.current_plan }}
                              </span>
                            </div>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <!-- Primary Contact Section -->
              <div>
                <Label class="mb-3 block text-sm font-medium text-gray-700">Primary Contact</Label>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <Label class="mb-1 block text-sm text-gray-600">First Name</Label>
                    <Input
                      v-model="contactFirstName"
                      placeholder="John"
                      :disabled="creating"
                    />
                  </div>
                  <div>
                    <Label class="mb-1 block text-sm text-gray-600">Last Name</Label>
                    <Input
                      v-model="contactLastName"
                      placeholder="Doe"
                      :disabled="creating"
                    />
                  </div>
                </div>
                <div class="mt-3">
                  <Label class="mb-1 block text-sm text-gray-600">Email</Label>
                  <Input
                    v-model="contactEmail"
                    type="email"
                    placeholder="john.doe@example.com"
                    :disabled="creating"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <!-- Footer with Create Button -->
      <div class="flex justify-end gap-3">
        <Button variant="outline" @click="open = false" :disabled="creating">
          Cancel
        </Button>
        <Button @click="createCustomer" :disabled="creating || !isFormValid">
          <Loader2 v-if="creating" class="mr-2 h-4 w-4 animate-spin" />
          Create Customer
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
